import type useOutgoingWebhookDeliveries from "@babackapp/private-api-client/client/use-outgoing-webhook-deliveries";
import type { useReactivateOutgoingWebhookEndpoint } from "@babackapp/private-api-client/client/use-outgoing-webhook-deliveries";
import type useOutgoingWebhookSecret from "@babackapp/private-api-client/client/use-outgoing-webhook-secret";
import type { useRegenerateOutgoingWebhookSecret } from "@babackapp/private-api-client/client/use-outgoing-webhook-secret";
import type useWorkflowWebhooks from "@babackapp/private-api-client/client/use-workflow-webhooks";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@babackapp/ui/components/alert-dialog";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";
import { Copy, Eye, EyeOff } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTriggerLabel } from "../listing/use-trigger-label";
import type { Workflow } from "../types";

/**
 * Host wiring: each app injects its own API hooks and router link, the same
 * way the editor core does. Hooks must be stable module-level references.
 */
export type WebhooksSettingsProps = {
  shopId: string;
  /** ADMIN/OWNER: can see and regenerate the secret, re-enable a URL. */
  canManage: boolean;
  api: {
    useSecret: typeof useOutgoingWebhookSecret;
    useRegenerateSecret: typeof useRegenerateOutgoingWebhookSecret;
    useWebhooks: typeof useWorkflowWebhooks;
    useDeliveries: typeof useOutgoingWebhookDeliveries;
    useReactivateEndpoint: typeof useReactivateOutgoingWebhookEndpoint;
  };
  renderWorkflowLink: (workflowId: string, label: string) => ReactNode;
  docsUrl?: string;
};

export function WebhooksSettings(props: WebhooksSettingsProps) {
  return (
    <div className="space-y-6">
      <SigningSecretSection {...props} />
      <WorkflowWebhooksSection {...props} />
      <DeliveriesSection {...props} />
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-sm font-medium">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function SigningSecretSection({
  shopId,
  canManage,
  api,
  docsUrl,
}: WebhooksSettingsProps) {
  const { t } = useTranslation("trackoffice");
  const [isRevealed, setIsRevealed] = useState(false);
  const { data } = api.useSecret(shopId, { enabled: canManage });
  const regenerate = api.useRegenerateSecret(shopId);
  const secret = data?.secret ?? "";

  return (
    <Section
      title={t("settings.webhooks.secret.title", "Signing secret")}
      description={
        <>
          {t(
            "settings.webhooks.secret.description",
            "Every webhook carries an X-Baback-Signature header computed with this secret. Check it on your side to be sure the request comes from Baback.",
          )}{" "}
          {docsUrl && (
            <a
              href={docsUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {t("settings.webhooks.secret.docsLink", "How to verify it")}
            </a>
          )}
        </>
      }
    >
      {canManage ? (
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={isRevealed ? secret : "•".repeat(Math.min(secret.length, 40))}
            className="font-mono text-xs"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setIsRevealed((value) => !value)}
            aria-label={t("settings.webhooks.secret.toggle", "Show or hide")}
          >
            {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={!secret}
            onClick={() => void navigator.clipboard.writeText(secret)}
            aria-label={t("settings.webhooks.secret.copy", "Copy")}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={regenerate.isPending}>
                {t("settings.webhooks.secret.regenerate", "Regenerate")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t(
                    "settings.webhooks.secret.regenerateTitle",
                    "Regenerate the signing secret?",
                  )}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "settings.webhooks.secret.regenerateWarning",
                    "The current secret stops working immediately. Every system that checks the signature must be updated with the new one.",
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  {t("common.cancel", "Cancel")}
                </AlertDialogCancel>
                <AlertDialogAction onClick={() => regenerate.mutate()}>
                  {t("settings.webhooks.secret.regenerate", "Regenerate")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t(
            "settings.webhooks.secret.adminOnly",
            "Only admins can see the secret.",
          )}
        </p>
      )}
    </Section>
  );
}

function WorkflowWebhooksSection({
  shopId,
  canManage,
  api,
  renderWorkflowLink,
}: WebhooksSettingsProps) {
  const { t, i18n } = useTranslation("trackoffice");
  const getTriggerLabel = useTriggerLabel();
  const { data: webhooks = [] } = api.useWebhooks(shopId);
  const reactivate = api.useReactivateEndpoint(shopId);

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString(i18n.language) : "—";

  return (
    <Section
      title={t("settings.webhooks.workflows.title", "Workflows sending a webhook")}
      description={t(
        "settings.webhooks.workflows.description",
        "A URL is switched off after a full day of failed deliveries (at least 10 events). Re-enable it once the receiver is fixed.",
      )}
    >
      {webhooks.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(
            "settings.webhooks.workflows.empty",
            "No workflow sends a webhook yet. Add a \"Send webhook\" action to a workflow to get started.",
          )}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("settings.webhooks.workflows.workflow", "Workflow")}</TableHead>
              <TableHead>{t("settings.webhooks.workflows.url", "URL")}</TableHead>
              <TableHead>{t("settings.webhooks.workflows.lastSuccess", "Last success")}</TableHead>
              <TableHead>{t("settings.webhooks.workflows.status", "Status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {webhooks.map((webhook) => {
              const trigger = getTriggerLabel(
                webhook.trigger as Workflow["trigger"],
              );
              const disabledAt = webhook.endpoint?.disabledAt;

              return (
                <TableRow key={`${webhook.workflowId}-${webhook.nodeId}`}>
                  <TableCell>
                    <div className="flex flex-col">
                      {renderWorkflowLink(webhook.workflowId, webhook.workflowName)}
                      <span className="text-xs text-muted-foreground">
                        {[trigger.typeLabel, trigger.summary]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[260px] truncate font-mono text-xs">
                    {webhook.url}
                  </TableCell>
                  <TableCell className="text-xs">
                    {formatDate(webhook.endpoint?.lastSuccessAt)}
                  </TableCell>
                  <TableCell>
                    {disabledAt ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive-outline" size="sm">
                          {t("settings.webhooks.workflows.disabled", "Disabled")}
                        </Badge>
                        {canManage && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={reactivate.isPending}
                            onClick={() => reactivate.mutate({ url: webhook.url })}
                          >
                            {t("settings.webhooks.workflows.reactivate", "Re-enable")}
                          </Button>
                        )}
                      </div>
                    ) : webhook.isActive ? (
                      <Badge variant="success-outline" size="sm">
                        {t("settings.webhooks.workflows.active", "Active")}
                      </Badge>
                    ) : (
                      <Badge variant="outline" size="sm">
                        {t("settings.webhooks.workflows.workflowOff", "Workflow off")}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

const DELIVERIES_PAGE_SIZE = 20;

function DeliveriesSection({ shopId, api }: WebhooksSettingsProps) {
  const { t, i18n } = useTranslation("trackoffice");
  const [page, setPage] = useState(1);
  const { data } = api.useDeliveries(shopId, {
    page,
    pageSize: DELIVERIES_PAGE_SIZE,
  });
  const deliveries = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <Section
      title={t("settings.webhooks.deliveries.title", "Delivery log")}
      description={t(
        "settings.webhooks.deliveries.description",
        "Every attempt of the last 30 days. The content sent is not kept.",
      )}
    >
      {deliveries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("settings.webhooks.deliveries.empty", "No delivery yet.")}
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("settings.webhooks.deliveries.date", "Date")}</TableHead>
                <TableHead>{t("settings.webhooks.deliveries.url", "URL")}</TableHead>
                <TableHead>{t("settings.webhooks.deliveries.attempt", "Attempt")}</TableHead>
                <TableHead>{t("settings.webhooks.deliveries.result", "Result")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((delivery) => (
                <TableRow key={delivery.id}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {new Date(delivery.createdAt).toLocaleString(i18n.language)}
                    {delivery.isTest && (
                      <Badge variant="outline" size="xs" className="ml-2">
                        {t("settings.webhooks.deliveries.test", "Test")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[220px] text-xs">
                    <div className="truncate font-mono">{delivery.url}</div>
                    {delivery.version != null && (
                      <span className="text-muted-foreground">
                        v{delivery.version}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {delivery.attempt || "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    <DeliveryResult delivery={delivery} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                {t("settings.webhooks.deliveries.previous", "Previous")}
              </Button>
              <span className="text-xs text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                {t("settings.webhooks.deliveries.next", "Next")}
              </Button>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

type Delivery = NonNullable<
  ReturnType<WebhooksSettingsProps["api"]["useDeliveries"]>["data"]
>["data"][number];

function DeliveryResult({ delivery }: { delivery: Delivery }) {
  const { t } = useTranslation("trackoffice");

  if (delivery.status === "SUCCESS") {
    return (
      <Badge variant="success-outline" size="sm">
        {delivery.statusCode ?? "OK"} · {delivery.durationMs} ms
      </Badge>
    );
  }

  if (delivery.status === "SKIPPED") {
    return (
      <Badge variant="outline" size="sm">
        {t("settings.webhooks.deliveries.skipped", "Skipped: URL disabled")}
      </Badge>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Badge variant="destructive-outline" size="sm" className="w-fit">
        {delivery.statusCode ?? t("settings.webhooks.deliveries.noResponse", "No response")}
      </Badge>
      {delivery.error && (
        <span
          className="max-w-[320px] truncate text-muted-foreground"
          title={delivery.error}
        >
          {delivery.error}
        </span>
      )}
    </div>
  );
}
